import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Box, Button, Flex, HStack, SimpleGrid, Text} from '@chakra-ui/react';
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {endOfMonth, format, startOfMonth} from 'date-fns';
import {getMoisLabels} from 'ff-common';
import {CalendarModale} from '@/components/Modale/CalendarModale/CalendarModale';
import {BlueButton} from '@/components/Button/BlueButton';
import {GrayButton} from '@/components/Button/GrayButton';

interface PeriodeMois {
  date_debut: string;
  date_fin: string;
}

/**
 * Modale de sélection de période mois par mois pour le dashboard ÉGalim.
 * Deux colonnes (Début / Fin) avec navigation par année et grille de 12 mois.
 * La sélection est limitée à l'année en cours et l'année précédente (cohérence Tolede).
 */
export const EgalimPeriodeModale = NiceModal.create(
  ({
    onSelect,
    initialPeriode,
  }: {
    onSelect: (periode: PeriodeMois) => void;
    initialPeriode: PeriodeMois;
  }) => {
    const modal = useModal();
    const {t} = useTranslation();

    // Parse initial values
    const initDebut = new Date(initialPeriode.date_debut);
    const initFin = new Date(initialPeriode.date_fin);

    // États locaux pour les sélections de début et fin
    const [anneeDebut, setAnneeDebut] = useState(initDebut.getFullYear());
    const [moisDebut, setMoisDebut] = useState(initDebut.getMonth());
    const [anneeFin, setAnneeFin] = useState(initFin.getFullYear());
    const [moisFin, setMoisFin] = useState(initFin.getMonth());

    const handleValidate = useCallback(() => {
      const debut = startOfMonth(new Date(anneeDebut, moisDebut));
      const fin = endOfMonth(new Date(anneeFin, moisFin));
      onSelect({
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      });
      modal.remove();
    }, [anneeDebut, moisDebut, anneeFin, moisFin, onSelect, modal]);

    const handleCancel = useCallback(() => {
      modal.remove();
    }, [modal]);

    // Vérifier que début <= fin
    const isValid =
      anneeDebut < anneeFin ||
      (anneeDebut === anneeFin && moisDebut <= moisFin);

    return (
      <CalendarModale
        modal={modal}
        messageKey='selection_periode'
        titre={t('egalim.selection_periode')}
        onCancel={handleCancel}
        size_desktop='2xl'
      >
        <Box>
          <Flex
            direction={{base: 'column', md: 'row'}}
            gap={6}
          >
            {/* Colonne Début */}
            <Box flex='1'>
              <Text
                fontWeight='600'
                mb={2}
              >
                {t('egalim.periode_debut')}
              </Text>
              <YearSelector
                annee={anneeDebut}
                onChange={setAnneeDebut}
                minAnnee={new Date().getFullYear() - 1} // Limitation à l'année en cours et l'année précédente (cohérence Tolede)
                maxAnnee={new Date().getFullYear()}
              />
              <MonthGrid
                selectedMois={moisDebut}
                onSelect={setMoisDebut}
              />
            </Box>

            {/* Colonne Fin */}
            <Box flex='1'>
              <Text
                fontWeight='600'
                mb={2}
              >
                {t('egalim.periode_fin')}
              </Text>
              <YearSelector
                annee={anneeFin}
                onChange={setAnneeFin}
                minAnnee={new Date().getFullYear() - 1} // Limitation à l'année en cours et l'année précédente (cohérence Tolede)
                maxAnnee={new Date().getFullYear()}
              />
              <MonthGrid
                selectedMois={moisFin}
                onSelect={setMoisFin}
              />
            </Box>
          </Flex>

          {/* Boutons */}
          <HStack
            mt={6}
            gap={3}
            flexDirection={{base: 'column', md: 'row'}}
            alignItems='stretch'
          >
            <GrayButton
              variant='outline'
              onClick={handleCancel}
              flex='1'
              mt='0'
              texte={t('global.annuler')}
            />
            <BlueButton
              texte={t('global.valider')}
              isDisabled={!isValid}
              onClick={handleValidate}
              flex='1'
              mt='0'
            />
          </HStack>
        </Box>
      </CalendarModale>
    );
  },
);

/** Sélecteur d'année avec flèches gauche/droite */
const YearSelector: React.FC<{
  annee: number;
  onChange: (annee: number) => void;
  minAnnee: number;
  maxAnnee: number;
}> = ({annee, onChange, minAnnee, maxAnnee}) => (
  <Flex
    align='center'
    justify='space-between'
    bg='gray.light'
    borderTopRadius='8px'
    px={2}
    py={1}
    mb={3}
  >
    <Button
      variant='unstyled'
      size='sm'
      color='gray.dark'
      minW='auto'
      isDisabled={annee <= minAnnee}
      onClick={() => {
        onChange(annee - 1);
      }}
    >
      <ChevronLeftIcon boxSize={8} />
    </Button>
    <Text
      fontWeight='600'
      color='gray.dark'
    >
      {annee}
    </Text>
    <Button
      variant='unstyled'
      size='sm'
      color='gray.dark'
      minW='auto'
      isDisabled={annee >= maxAnnee}
      onClick={() => {
        onChange(annee + 1);
      }}
    >
      <ChevronRightIcon boxSize={8} />
    </Button>
  </Flex>
);

/** Grille 4x3 de mois */
const MonthGrid: React.FC<{
  selectedMois: number;
  onSelect: (mois: number) => void;
}> = ({selectedMois, onSelect}) => {
  const mois = getMoisLabels();
  return (
    <SimpleGrid
      columns={3}
      spacing={2}
    >
      {mois.map((label, index) => {
        const isSelected = index === selectedMois;
        return (
          <Button
            key={label}
            size='sm'
            variant={isSelected ? 'solid' : 'ghost'}
            bg={isSelected ? 'blue.main' : 'transparent'}
            color={isSelected ? 'white' : 'gray.dark'}
            fontWeight={isSelected ? '600' : '400'}
            _hover={{bg: isSelected ? 'blue.dark' : 'gray.100'}}
            borderRadius='md'
            onClick={() => {
              onSelect(index);
            }}
          >
            {label}
          </Button>
        );
      })}
    </SimpleGrid>
  );
};
